import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Leaf, Cpu, Satellite, Bot, BrainCircuit, ArrowRight, User, Phone, MapPin, Wheat, CheckCircle, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../config';
import Navbar from '../components/Navbar';

const LandingPage = () => {
  const navigate = useNavigate();
  const [farmerForm, setFarmerForm] = useState({
    name: '', phone: '', village: '', state: '', crop_type: '', land_size: '', notes: ''
  });
  const [formStatus, setFormStatus] = useState({ loading: false, success: false, error: '' });

  const handleFarmerSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, success: false, error: '' });
    try {
      const response = await fetch(`${BACKEND_URL}/api/farmer/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(farmerForm)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Registration failed');
      setFormStatus({ loading: false, success: true, error: '' });
      setFarmerForm({ name: '', phone: '', village: '', state: '', crop_type: '', land_size: '', notes: '' });
    } catch (err) {
      setFormStatus({ loading: false, success: false, error: err.message });
    }
  };

  return (
    <div className="landing-page">
      <Navbar />

      {/* Hero Section */}
      <section className="hero-section value-chain-hero">
        <div className="hero-overlay"></div>

        <div className="floating-elements">
          <div className="float-tech float-tech-1">
            <img src="https://images.pexels.com/photos/16678079/pexels-photo-16678079.jpeg?auto=compress&cs=tinysrgb&w=200" alt="Farmer using AI" />
          </div>
          <div className="float-tech float-tech-2">
            <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200" alt="AI Analytics" />
          </div>
          <div className="float-tech float-tech-3">
            <img src="https://images.pexels.com/photos/3912469/pexels-photo-3912469.jpeg?auto=compress&cs=tinysrgb&w=200" alt="Crop AI" />
          </div>
        </div>

        <div className="hero-content">
          <div className="hero-text">
            <div className="logo-large">
              <img
                src="https://customer-assets.emergentagent.com/job_21bfd8d3-1f77-4e74-9916-0e33dfa0e394/artifacts/2bnzvx47_logo.png"
                alt="BoAI Logo"
                className="boai-logo-img"
              />
            </div>
            <p className="brand-tagline">Better Farming Better Future</p>

            <div className="hero-tagline-badge">
              <BrainCircuit size={16} /> AI-POWERED SUPPLY CHAIN MANAGEMENT
            </div>

            <h2 className="hero-title-new">Intelligent Food Chains, Smarter Farms</h2>
            <h3 className="hero-subtitle-new">खेत से समृद्धि तक | Khet se Samriddhi tak</h3>

            <p className="hero-description-new">
              Building the future of agricultural supply chain with AI, drones, and robotics.
              From farm to fork — smarter, faster, with zero wastage.
            </p>
            <p className="hero-description-hi">
              AI, ड्रोन और रोबोटिक्स के साथ कृषि आपूर्ति श्रृंखला का भविष्य बना रहे हैं। खेत से थाली तक — स्मार्ट, तेज़, शून्य बर्बादी।
            </p>

            <div className="tech-badges-row">
              <div className="tech-badge-item"><Cpu size={18} /><span>AI Grading</span></div>
              <div className="tech-badge-item"><Satellite size={18} /><span>Drone Monitoring</span></div>
              <div className="tech-badge-item"><Bot size={18} /><span>Smart Robotics</span></div>
            </div>

            <div className="hero-buttons">
              <button
                className="btn-primary btn-large"
                onClick={() => document.getElementById('farmer-register').scrollIntoView({ behavior: 'smooth' })}
                data-testid="join-network-btn"
              >
                Join Our Network
              </button>
              <button
                className="btn-secondary btn-large"
                onClick={() => navigate('/our-process')}
              >
                See How It Works
              </button>
            </div>
          </div>

          <div className="hero-visual">
            <div className="value-chain-preview">
              <div className="chain-flow">
                <div className="chain-node farmer-node">
                  <div className="node-icon">👨‍🌾</div>
                  <span>Farmers</span>
                </div>
                <div className="chain-arrow"><ArrowRight size={24} /></div>
                <div className="chain-node boai-node">
                  <div className="node-icon"><Leaf size={28} /></div>
                  <span>BoAI</span>
                </div>
                <div className="chain-arrow"><ArrowRight size={24} /></div>
                <div className="chain-node buyer-node">
                  <div className="node-icon">🏪</div>
                  <span>Buyers</span>
                </div>
              </div>
              <div className="chain-tagline">AI-Powered Agricultural Supply Chain</div>
            </div>
          </div>
        </div>
      </section>

      {/* Ticker Banner */}
      <div className="ticker-banner">
        <div className="ticker-track">
          <div className="ticker-content">
            <span>🌾 Fresh from Farms</span><span>•</span>
            <span>✅ Quality Assured</span><span>•</span>
            <span>🚛 Fast Delivery</span><span>•</span>
            <span>💰 Fair Prices</span><span>•</span>
            <span>🤝 Direct Connect</span><span>•</span>
            <span>📦 Zero Wastage</span><span>•</span>
            <span>🌾 Fresh from Farms</span><span>•</span>
            <span>✅ Quality Assured</span><span>•</span>
            <span>🚛 Fast Delivery</span><span>•</span>
            <span>💰 Fair Prices</span><span>•</span>
            <span>🤝 Direct Connect</span><span>•</span>
            <span>📦 Zero Wastage</span><span>•</span>
          </div>
        </div>
      </div>

      {/* Aggregation Promise Banner */}
      <section className="aggregation-promise">
        <div className="container">
          <div className="promise-content">
            <h2>We Help Aggregate Using AI & Give Fair Price with Doorstep Pickup</h2>
            <h3 className="hindi-text">हम AI का उपयोग करके एकत्रीकरण करते हैं और उचित मूल्य और घर पर पिकअप देते हैं</h3>
            <div className="promise-items">
              <div className="promise-item"><span className="promise-icon">🤖</span><span>AI-powered crop grading & fair pricing</span></div>
              <div className="promise-item"><span className="promise-icon">🚛</span><span>Doorstep pickup from farmer's field</span></div>
              <div className="promise-item"><span className="promise-icon">💰</span><span>Instant payment, no middlemen</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Showcase — AI Focused */}
      <section className="tech-showcase-section">
        <div className="container">
          <div className="section-header-centered">
            <div className="section-badge"><BrainCircuit size={14} /> OUR FOCUS AREAS</div>
            <h2>AI at the Core of Everything We Build</h2>
            <h3 className="hindi-text">हम जो कुछ भी बनाते हैं उसके केंद्र में AI है</h3>
            <p className="tech-section-subtitle">From mobile-first farmer tools to enterprise analytics — every product is powered by intelligence</p>
          </div>

          <div className="tech-grid tech-grid-5">
            <div className="tech-card">
              <div className="tech-card-image">
                <img src="https://images.pexels.com/photos/16678079/pexels-photo-16678079.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Farmer AI Mobile" />
                <div className="tech-card-overlay"><Cpu size={28} /></div>
              </div>
              <div className="tech-card-content">
                <h4>AI on Mobile</h4>
                <p>Farmers scan crops, get instant AI diagnosis and treatment plans — right from their smartphone</p>
              </div>
            </div>

            <div className="tech-card">
              <div className="tech-card-image">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400" alt="Data Analytics" />
                <div className="tech-card-overlay"><BrainCircuit size={28} /></div>
              </div>
              <div className="tech-card-content">
                <h4>Predictive Analytics</h4>
                <p>Demand forecasting, price prediction, and supply chain optimization using machine learning</p>
              </div>
            </div>

            <div className="tech-card">
              <div className="tech-card-image">
                <img src="https://images.pexels.com/photos/3912469/pexels-photo-3912469.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Smart Crop Analysis" />
                <div className="tech-card-overlay"><Satellite size={28} /></div>
              </div>
              <div className="tech-card-content">
                <h4>Smart Crop Monitoring</h4>
                <p>Satellite imagery, NDVI mapping, and drone surveillance for precision agriculture</p>
              </div>
            </div>

            <div className="tech-card">
              <div className="tech-card-image">
                <img src="https://images.pexels.com/photos/34182314/pexels-photo-34182314.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Agricultural Drones" />
                <div className="tech-card-overlay"><Satellite size={28} /></div>
              </div>
              <div className="tech-card-content">
                <h4>Agricultural Drones</h4>
                <p>Aerial crop monitoring, precision spraying, and field mapping for optimized farming</p>
              </div>
            </div>

            <div className="tech-card">
              <div className="tech-card-image">
                <img src="https://images.unsplash.com/photo-1627920769842-6887c6df05ca?w=400" alt="Smart Robotics" />
                <div className="tech-card-overlay"><Bot size={28} /></div>
              </div>
              <div className="tech-card-content">
                <h4>Smart Robotics</h4>
                <p>Automated harvesting, sorting, and quality grading to reduce waste and labor costs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation Cards */}
      <section className="explore-section">
        <div className="container">
          <div className="section-header-centered">
            <h2>Explore BoAI</h2>
            <p className="tech-section-subtitle">Discover what makes us different</p>
          </div>
          <div className="explore-grid">
            <Link to="/our-process" className="explore-card" data-testid="explore-our-process">
              <div className="explore-icon">🔄</div>
              <h4>Our Process</h4>
              <p>Farm to market value chain</p>
            </Link>
            <Link to="/why-boai" className="explore-card" data-testid="explore-why-boai">
              <div className="explore-icon">💡</div>
              <h4>Why BoAI</h4>
              <p>Competitive advantages</p>
            </Link>
            <Link to="/ai-solutions" className="explore-card" data-testid="explore-ai-solutions">
              <div className="explore-icon">🧠</div>
              <h4>AI Solutions</h4>
              <p>Disease detection & more</p>
            </Link>
            <Link to="/farm-insights-info" className="explore-card" data-testid="explore-farm-insights">
              <div className="explore-icon">🌾</div>
              <h4>Farm Insights</h4>
              <p>Soil, weather, market prices</p>
            </Link>
            <Link to="/community-info" className="explore-card" data-testid="explore-community">
              <div className="explore-icon">💬</div>
              <h4>Community</h4>
              <p>Join the farmer network</p>
            </Link>
            <Link to="/marketplace-info" className="explore-card" data-testid="explore-marketplace">
              <div className="explore-icon">🛒</div>
              <h4>Marketplace</h4>
              <p>Agricultural e-commerce</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Farmer Registration Form */}
      <section className="farmer-aggregation-section" id="farmer-register">
        <div className="container">
          <div className="aggregation-showcase">
            <div className="aggregation-content">
              <div className="aggregation-header">
                <div className="aggregation-badge">🤝 राष्ट्रीय स्तर की पहल | NATIONAL INITIATIVE</div>
                <h2>किसान एकीकरण एवं थोक बाजार</h2>
                <h3>Farmer Aggregation & Bulk Market Access</h3>
                <p className="aggregation-tagline">
                  BOAI किसानों को जोड़कर उनकी फसल को संगठित रूप से बाजार तक पहुँचाता है।
                </p>
                <p className="aggregation-tagline-en">
                  BoAI is aggregating farmers and organizing crop supply to bulk buyers across India.
                  Share your details — our team will contact you.
                </p>
              </div>

              <div className="aggregation-benefits">
                <div className="benefit-item">
                  <div className="benefit-icon">💰</div>
                  <div className="benefit-text">
                    <h4>बेहतर मूल्य</h4>
                    <p>Better prices through collective bargaining</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🚛</div>
                  <div className="benefit-text">
                    <h4>सीधी बाजार पहुँच</h4>
                    <p>Direct market access, no middlemen</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">📱</div>
                  <div className="benefit-text">
                    <h4>तकनीकी सहायता</h4>
                    <p>Free technology and AI support</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="aggregation-form-wrapper">
              <form className="farmer-form" onSubmit={handleFarmerSubmit} data-testid="farmer-registration-form">
                <h3 className="form-title">अपनी फसल को बाज़ार से जोड़ें</h3>
                <p className="form-subtitle">Connect your crops to the market</p>

                {formStatus.success && (
                  <div className="form-success" data-testid="farmer-form-success">
                    <CheckCircle size={20} />
                    <div>
                      <p><strong>सफलतापूर्वक पंजीकृत!</strong></p>
                      <p>Registration successful! Our team will contact you soon.</p>
                    </div>
                  </div>
                )}
                {formStatus.error && (
                  <div className="form-error" data-testid="farmer-form-error">
                    <p>{formStatus.error}</p>
                  </div>
                )}

                <div className="form-grid">
                  <div className="form-group">
                    <label><User size={14} /> नाम / Name *</label>
                    <input type="text" value={farmerForm.name} onChange={(e) => setFarmerForm({...farmerForm, name: e.target.value})} placeholder="आपका नाम" required data-testid="farmer-name-input" />
                  </div>
                  <div className="form-group">
                    <label><Phone size={14} /> फोन / Phone *</label>
                    <input type="tel" value={farmerForm.phone} onChange={(e) => setFarmerForm({...farmerForm, phone: e.target.value})} placeholder="मोबाइल नंबर" required data-testid="farmer-phone-input" />
                  </div>
                  <div className="form-group">
                    <label><MapPin size={14} /> गाँव / Village *</label>
                    <input type="text" value={farmerForm.village} onChange={(e) => setFarmerForm({...farmerForm, village: e.target.value})} placeholder="गाँव का नाम" required data-testid="farmer-village-input" />
                  </div>
                  <div className="form-group">
                    <label><MapPin size={14} /> राज्य / State *</label>
                    <select value={farmerForm.state} onChange={(e) => setFarmerForm({...farmerForm, state: e.target.value})} required data-testid="farmer-state-select">
                      <option value="">राज्य चुनें / Select State</option>
                      <option value="Uttar Pradesh">उत्तर प्रदेश</option>
                      <option value="Madhya Pradesh">मध्य प्रदेश</option>
                      <option value="Maharashtra">महाराष्ट्र</option>
                      <option value="Rajasthan">राजस्थान</option>
                      <option value="Bihar">बिहार</option>
                      <option value="Gujarat">गुजरात</option>
                      <option value="Karnataka">कर्नाटक</option>
                      <option value="Punjab">पंजाब</option>
                      <option value="Haryana">हरियाणा</option>
                      <option value="Tamil Nadu">तमिल नाडु</option>
                      <option value="Other">अन्य / Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label><Wheat size={14} /> फसल / Crop *</label>
                    <input type="text" value={farmerForm.crop_type} onChange={(e) => setFarmerForm({...farmerForm, crop_type: e.target.value})} placeholder="गेहूं, चावल, आलू..." required data-testid="farmer-crop-input" />
                  </div>
                  <div className="form-group">
                    <label>जमीन / Land (acres)</label>
                    <input type="text" value={farmerForm.land_size} onChange={(e) => setFarmerForm({...farmerForm, land_size: e.target.value})} placeholder="एकड़ में" data-testid="farmer-land-input" />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>अतिरिक्त जानकारी / Notes</label>
                  <textarea value={farmerForm.notes} onChange={(e) => setFarmerForm({...farmerForm, notes: e.target.value})} placeholder="कोई अन्य जानकारी..." rows="2" data-testid="farmer-notes-input" />
                </div>

                <button type="submit" className="farmer-submit-btn" disabled={formStatus.loading} data-testid="farmer-submit-btn">
                  {formStatus.loading ? (<><Loader2 size={18} className="spin" /> पंजीकरण हो रहा है...</>) : ('पंजीकरण करें / Register Now')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_21bfd8d3-1f77-4e74-9916-0e33dfa0e394/artifacts/2bnzvx47_logo.png"
                alt="BoAI"
                className="footer-logo-img"
              />
            </div>
            <p className="footer-tagline">Better Farming Better Future</p>
            <p>AI-Powered Supply Chain Management | खेत से समृद्धि तक</p>
            <div className="footer-nav">
              <Link to="/about">About</Link>
              <Link to="/our-process">Our Process</Link>
              <Link to="/why-boai">Why BoAI</Link>
              <Link to="/ai-solutions">AI Solutions</Link>
              <Link to="/privacy">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
